import PropTypes from 'prop-types';
import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import ScreenReaderMessage from '@bibliocommons/base-screen-reader-message';
import { paginationShape, listShape } from '@bibliocommons/bc-prop-types';

import { Table, TableHeader, TableHeading, TableBody, TableRow, TableCell } from 'app/components/shared/Table';
import ShortFormattedDate from 'app/components/shared/ShortFormattedDate';
import { selectListItemEntities } from 'app/selectors/EntitiesSelector';
import Pagination, { PaginationLabel } from 'app/components/shared/Pagination';
import AddToListButton from '../AddToListButton';

import './AddToListTable.scss';

export default function AddToListTable({
  bibTitle,
  handlePageClick,
  renderEmptyContent,
  lists,
  metadataId,
  pagination,
  isFetching,
  trackAddToListClick
}) {
  const listItemEntities = useSelector(selectListItemEntities);
  const groupedListItems = listItemEntities.toList().groupBy(listItem => listItem.get('listId'));

  function renderAddToListButton(list) {
    const listItems = groupedListItems.get(list.get('draftListId') || list.get('id')) || Immutable.List();
    const listItemForBib = listItems.find(listItem => listItem.getIn(['bibListItem', 'id']) === metadataId);

    return (
      <AddToListButton
        bibTitle={bibTitle}
        list={list}
        metadataId={metadataId}
        existingListItem={listItemForBib}
        trackClick={trackAddToListClick}
      />
    );
  }

  function renderTable() {
    return (
      <div className="lists-table">
        <PaginationLabel pagination={pagination} textKey="lists_pagination_info" router={false} autoFocus />
        <Table>
          <TableHeader>
            <TableHeading className="list-name-col">
              <FormattedMessage id="list_name" />
            </TableHeading>
            <TableHeading className="list-date-col">
              <FormattedMessage id="list_updated_date" />
            </TableHeading>
            <TableHeading className="list-action-col">
              <ScreenReaderMessage>
                <FormattedMessage id="list_action_and_status" />
              </ScreenReaderMessage>
            </TableHeading>
          </TableHeader>
          <TableBody>
            {lists.map(list => (
              <TableRow className="list-row" key={list.get('id')}>
                <TableCell className="list-name-col" label={<FormattedMessage id="list_name" />} blockLabel>
                  {list.get('name') || <FormattedMessage id="untitled_list" />}
                </TableCell>
                <TableCell className="list-date-col" label={<FormattedMessage id="list_updated_date" />} blockLabel>
                  <ShortFormattedDate value={list.get('updated')} />
                </TableCell>
                <TableCell
                  className="list-action-col"
                  label={
                    <ScreenReaderMessage>
                      <FormattedMessage id="list_action_and_status" />
                    </ScreenReaderMessage>
                  }
                  blockLabel
                >
                  {renderAddToListButton(list)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Pagination
          handleClick={handlePageClick}
          pagination={pagination}
          textKey="lists_pagination_info"
          isFetching={isFetching}
          router={false}
        />
      </div>
    );
  }

  return <div className="cp-add-to-list-table">{lists.size > 0 ? renderTable() : renderEmptyContent()}</div>;
}

AddToListTable.propTypes = {
  bibTitle: PropTypes.string.isRequired,
  lists: ImmutablePropTypes.listOf(listShape).isRequired,
  metadataId: PropTypes.string.isRequired,
  pagination: paginationShape.isRequired,
  isFetching: PropTypes.bool.isRequired,
  handlePageClick: PropTypes.func.isRequired,
  renderEmptyContent: PropTypes.func.isRequired,
  trackAddToListClick: PropTypes.func.isRequired
};
